/**
 * A crystal charge's values, each as a picture: its fuse as a bar burning towards the moment it
 * goes off, the rock it breaks as chips (each with the block it gives), and for a charge that
 * reaches round it, its area as a little map in its shape on the planet, the rock it breaks lit.
 */
import { BLOCK_OF } from '@bramvasterd/terrella-rules';
import type { JSX } from 'react';

import type { ChargeInfo } from '@game/planet/machines';
import type { ReachTile } from '@game/planet/reach';
import { duration, kindName, t } from '@i18n/index';
import { Icon, ResourceIcon } from '@ui/icons';

import { ReachMap } from './ReachMap';

export function ChargeStats({
  info,
  area,
  tile,
}: {
  info: ChargeInfo;
  area: readonly ReachTile[];
  tile: number;
}): JSX.Element {
  const burnt = info.fuseMs > 0 ? 1 - Math.min(1, info.left / info.fuseMs) : 1;
  const progress = Math.round(burnt * 100);
  return (
    <div className="xstats">
      <div className="holds fuse" data-stat="fuse">
        <div className="stat-head">
          <span className="k">{t('info.fuse')}</span>
          <small className="time">
            <Icon id="clock" />
            {t('info.fuse-left', { time: duration(info.left) })}
          </small>
        </div>
        <div
          className="upgrade-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
          aria-label={t('info.fuse')}
        >
          <i className="fill" style={{ width: `${String(Math.max(3, progress))}%` }} />
        </div>
      </div>
      <div className="holds" data-stat="breaks">
        <div className="stat-head">
          <span className="k">{t('info.breaks')}</span>
          <small>{t(info.range > 0 ? 'info.area-ring' : 'info.area-own')}</small>
        </div>
        <ul className="block-chips" aria-label={t('info.breaks')}>
          {info.breaks.map((kind) => (
            <li key={kind} data-kind={kind}>
              <ResourceIcon kind={BLOCK_OF.get(kind) ?? ''} />
              <small>{kindName(kind).toLowerCase()}</small>
            </li>
          ))}
        </ul>
      </div>
      {info.range > 0 && (
        <div className="reach wide" data-stat="area">
          <span className="k">{t('info.area')}</span>
          <ReachMap tiles={area} self={tile} />
          <b>{info.tiles}</b>
          <small>{t('info.tiles-broken', { n: info.tiles })}</small>
        </div>
      )}
    </div>
  );
}
